'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Crown,
  Lightbulb,
  TrendingUp,
  Rocket,
  DollarSign,
  FileText,
  Brain,
  Handshake,
} from 'lucide-react';

interface Stakeholder {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  position: { x: number; y: number };
  color: string;
  gives: string;
  gets: string;
}

const stakeholders: Stakeholder[] = [
  {
    id: 'builders',
    title: 'Builders',
    subtitle: 'Startup Founders',
    icon: <Rocket className="w-6 h-6" />,
    position: { x: 15, y: 25 },
    color: 'purple',
    gives: 'Equity (optional) & Fees',
    gets: 'Clarity, Execution Support & Structured Investor Matchmaking',
  },
  {
    id: 'investors',
    title: 'Capital Partners',
    subtitle: 'Investors',
    icon: <TrendingUp className="w-6 h-6" />,
    position: { x: 85, y: 25 },
    color: 'amber',
    gives: 'Investment Capital & Fees',
    gets: 'Investor Thesis, Structured Diligence & Curated Deal Flow',
  },
  {
    id: 'robinhoods',
    title: 'Robin Hoods',
    subtitle: 'Robinhood',
    icon: <Crown className="w-6 h-6" />,
    position: { x: 15, y: 75 },
    color: 'violet',
    gives: 'Strategy & Judgment',
    gets: 'Protected, Project-Based Income',
  },
  {
    id: 'insights',
    title: 'Zero Associates',
    subtitle: 'Zero Analysts',
    icon: <Lightbulb className="w-6 h-6" />,
    position: { x: 85, y: 75 },
    color: 'fuchsia',
    gives: 'Research & Analysis',
    gets: 'Paid, Real-World Experience & Credible Signal',
  },
];

// Floating value icons that travel along paths
function FloatingIcon({
  icon,
  delay,
  duration,
  path
}: {
  icon: React.ReactNode;
  delay: number;
  duration: number;
  path: string;
}) {
  return (
    <div
      className="absolute w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-purple-600 z-20"
      style={{
        offsetPath: `path('${path}')`,
        animation: `flowAlongPath ${duration}s linear ${delay}s infinite`,
      }}
    >
      {icon}
    </div>
  );
}

// Connection path between nodes
function ConnectionPath({
  from,
  to,
  isActive,
  flowDirection,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  isActive: boolean;
  flowDirection: 'to' | 'from' | 'both';
}) {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const curveFactor = 15;

  // Create a curved path
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const perpX = -dy * 0.15;
  const perpY = dx * 0.15;

  const pathD = `M ${from.x} ${from.y} Q ${midX + perpX} ${midY + perpY} ${to.x} ${to.y}`;

  return (
    <g>
      {/* Base path */}
      <path
        d={pathD}
        fill="none"
        stroke={isActive ? 'url(#activeGradient)' : 'rgba(147, 51, 234, 0.15)'}
        strokeWidth={isActive ? 3 : 1.5}
        strokeDasharray={isActive ? '0' : '6 4'}
        className="transition-all duration-500"
      />
      {/* Animated flow dots */}
      {isActive && (
        <>
          <circle r="4" fill="url(#activeGradient)">
            <animateMotion dur="2s" repeatCount="indefinite" path={pathD} />
          </circle>
          <circle r="4" fill="url(#activeGradient)">
            <animateMotion dur="2s" repeatCount="indefinite" path={pathD} begin="0.66s" />
          </circle>
          <circle r="4" fill="url(#activeGradient)">
            <animateMotion dur="2s" repeatCount="indefinite" path={pathD} begin="1.33s" />
          </circle>
        </>
      )}
    </g>
  );
}

// Stakeholder Node Component
function StakeholderNode({
  stakeholder,
  isActive,
  isHovered,
  onHover,
  onLeave,
}: {
  stakeholder: Stakeholder;
  isActive: boolean;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}) {
  const colorClasses = {
    purple: {
      bg: 'from-purple-500 to-purple-600',
      ring: 'ring-purple-200',
      glow: 'shadow-purple-500/30',
      text: 'text-purple-600',
      light: 'bg-purple-50',
    },
    amber: {
      bg: 'from-amber-400 to-amber-500',
      ring: 'ring-amber-200',
      glow: 'shadow-amber-500/30',
      text: 'text-amber-600',
      light: 'bg-amber-50',
    },
    violet: {
      bg: 'from-violet-500 to-violet-600',
      ring: 'ring-violet-200',
      glow: 'shadow-violet-500/30',
      text: 'text-violet-600',
      light: 'bg-violet-50',
    },
    fuchsia: {
      bg: 'from-fuchsia-500 to-fuchsia-600',
      ring: 'ring-fuchsia-200',
      glow: 'shadow-fuchsia-500/30',
      text: 'text-fuchsia-600',
      light: 'bg-fuchsia-50',
    },
  };

  const colors = colorClasses[stakeholder.color as keyof typeof colorClasses];

  return (
    <div
      className={cn(
        'absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 cursor-pointer z-10',
        isHovered && 'scale-110 z-30'
      )}
      style={{
        left: `${stakeholder.position.x}%`,
        top: `${stakeholder.position.y}%`,
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      {/* Glow effect */}
      <div
        className={cn(
          'absolute inset-0 rounded-2xl blur-xl transition-opacity duration-500',
          `bg-gradient-to-br ${colors.bg}`,
          isHovered ? 'opacity-40 scale-150' : 'opacity-0'
        )}
      />

      {/* Card */}
      <div
        className={cn(
          'relative bg-white rounded-2xl p-5 transition-all duration-500',
          'border-2 shadow-lg',
          isHovered
            ? `border-transparent ring-4 ${colors.ring} ${colors.glow} shadow-2xl`
            : 'border-gray-100'
        )}
        style={{ width: '100%', maxWidth: '220px', minHeight: isHovered ? '240px' : 'auto' }}
      >
        {/* Icon */}
        <div
          className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all duration-300',
            isHovered
              ? `bg-gradient-to-br ${colors.bg} text-white`
              : `${colors.light} ${colors.text}`
          )}
        >
          {stakeholder.icon}
        </div>

        {/* Title */}
        <h3 className="font-display font-semibold text-gray-900 text-base mb-0.5">
          {stakeholder.title}
        </h3>
        <p className="text-xs text-gray-500 mb-3">{stakeholder.subtitle}</p>

        {/* Value exchange - shows on hover */}
        <div
          className={cn(
            'space-y-3 overflow-visible transition-all duration-500',
            isHovered ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="flex items-start gap-2 text-xs">
            <span className="w-12 flex-shrink-0 text-gray-400">Gives:</span>
            <span className={cn('font-medium leading-relaxed', colors.text)}>{stakeholder.gives}</span>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <span className="w-12 flex-shrink-0 text-gray-400">Gets:</span>
            <span className="font-medium text-gray-700 leading-relaxed">{stakeholder.gets}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Central Hub
function CentralHub({ isActive }: { isActive: boolean }) {
  return (
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
      {/* Outer rings */}
      <div
        className={cn(
          'absolute inset-0 rounded-full border-2 border-purple-200 transition-all duration-1000',
          isActive ? 'scale-[2.5] opacity-0' : 'scale-100 opacity-100'
        )}
        style={{ width: '120px', height: '120px', margin: '-60px 0 0 -60px' }}
      />
      <div
        className={cn(
          'absolute inset-0 rounded-full border-2 border-purple-300 transition-all duration-1000 delay-200',
          isActive ? 'scale-[2] opacity-0' : 'scale-100 opacity-100'
        )}
        style={{ width: '100px', height: '100px', margin: '-50px 0 0 -50px' }}
      />

      {/* Core */}
      <div className="relative">
        {/* Animated glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 blur-lg opacity-40 animate-pulse"
          style={{ width: '80px', height: '80px', margin: '-40px 0 0 -40px' }}
        />

        {/* Hub content */}
        <div
          className="relative w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 via-violet-600 to-purple-700 flex items-center justify-center shadow-2xl shadow-purple-500/40"
          style={{ margin: '-40px 0 0 -40px' }}
        >
          <div className="text-center">
            <div className="text-white font-display font-bold text-xs tracking-wider">
              PURPLE
            </div>
            <div className="text-purple-200 text-[9px] tracking-widest">
              ENGINE
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EcosystemSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [animationStage, setAnimationStage] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Staggered animation stages
            setTimeout(() => setAnimationStage(1), 300);
            setTimeout(() => setAnimationStage(2), 600);
            setTimeout(() => setAnimationStage(3), 900);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Connection definitions (from hub to each node, and between related nodes)
  const connections = [
    { from: { x: 50, y: 50 }, to: { x: 15, y: 25 }, nodes: ['hub', 'builders'] },
    { from: { x: 50, y: 50 }, to: { x: 85, y: 25 }, nodes: ['hub', 'investors'] },
    { from: { x: 50, y: 50 }, to: { x: 15, y: 75 }, nodes: ['hub', 'robinhoods'] },
    { from: { x: 50, y: 50 }, to: { x: 85, y: 75 }, nodes: ['hub', 'insights'] },
    // Cross connections
    { from: { x: 15, y: 25 }, to: { x: 85, y: 25 }, nodes: ['builders', 'investors'] },
    { from: { x: 15, y: 25 }, to: { x: 15, y: 75 }, nodes: ['builders', 'robinhoods'] },
    { from: { x: 85, y: 25 }, to: { x: 85, y: 75 }, nodes: ['investors', 'insights'] },
    { from: { x: 15, y: 75 }, to: { x: 85, y: 75 }, nodes: ['robinhoods', 'insights'] },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-16 lg:py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, hsl(var(--warm-white)) 0%, hsl(270 50% 98%) 50%, hsl(var(--warm-white)) 100%)',
      }}
    >
      {/* Topographic background pattern */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" viewBox="0 0 1000 1000">
          {[...Array(20)].map((_, i) => (
            <ellipse
              key={i}
              cx="500"
              cy="500"
              rx={100 + i * 45}
              ry={80 + i * 35}
              fill="none"
              stroke="hsl(271 81% 56%)"
              strokeWidth="1"
              transform={`rotate(${i * 3} 500 500)`}
            />
          ))}
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-6 lg:mb-10">
          <div
            className={cn(
              'transition-all duration-700',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider mb-4">
              The Ecosystem
            </p>
            <h2
              className="font-display font-semibold text-gray-900 mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Four Forces,{' '}
              <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                One RaaS Engine
              </span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Purple Venture Studio aligns builders, capital, intelligence, and expertise—so every participant gives value and receives measurable outcomes. Hover over each role to see the exchange.
            </p>
          </div>
        </div>

        {/* Network Visualization */}
        <div
          className={cn(
            'relative mx-auto transition-all duration-1000',
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          )}
          style={{ maxWidth: '900px', height: '400px', minHeight: '350px' }}
        >
          {/* SVG Connection Lines */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="activeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="hsl(271 81% 56%)" />
                <stop offset="50%" stopColor="hsl(280 80% 60%)" />
                <stop offset="100%" stopColor="hsl(271 81% 56%)" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="1" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Render connections */}
            {animationStage >= 2 &&
              connections.map((conn, index) => (
                <ConnectionPath
                  key={index}
                  from={conn.from}
                  to={conn.to}
                  isActive={
                    hoveredNode !== null &&
                    conn.nodes.includes(hoveredNode)
                  }
                  flowDirection="both"
                />
              ))}
          </svg>

          {/* Central Hub */}
          {animationStage >= 1 && (
            <CentralHub isActive={hoveredNode !== null} />
          )}

          {/* Stakeholder Nodes */}
          {animationStage >= 3 &&
            stakeholders.map((stakeholder) => (
              <StakeholderNode
                key={stakeholder.id}
                stakeholder={stakeholder}
                isActive={hoveredNode === stakeholder.id}
                isHovered={hoveredNode === stakeholder.id}
                onHover={() => setHoveredNode(stakeholder.id)}
                onLeave={() => setHoveredNode(null)}
              />
            ))}

          {/* Value flow icons - animated along paths when no node is hovered */}
          {animationStage >= 3 && !hoveredNode && (
            <div className="absolute inset-0 pointer-events-none">
              {/* These are decorative animated icons */}
              <div
                className="absolute w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-purple-500 animate-float"
                style={{ top: '35%', left: '30%', animationDelay: '0s' }}
              >
                <DollarSign className="w-4 h-4" />
              </div>
              <div
                className="absolute w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-amber-500 animate-float"
                style={{ top: '30%', left: '65%', animationDelay: '0.5s' }}
              >
                <FileText className="w-4 h-4" />
              </div>
              <div
                className="absolute w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-violet-500 animate-float"
                style={{ top: '65%', left: '35%', animationDelay: '1s' }}
              >
                <Brain className="w-4 h-4" />
              </div>
              <div
                className="absolute w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-fuchsia-500 animate-float"
                style={{ top: '60%', left: '60%', animationDelay: '1.5s' }}
              >
                <Handshake className="w-4 h-4" />
              </div>
            </div>
          )}
        </div>

        {/* Bottom tagline */}
        <div
          className={cn(
            'text-center mt-12 transition-all duration-700 delay-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <p className="text-lg text-gray-500 font-serif italic max-w-2xl mx-auto">
            Purple Venture Studio aligns builders, capital, intelligence, and expertise—so every participant gives value and receives measurable outcomes.
          </p>
        </div>
      </div>

      {/* Add keyframe for flow animation */}
      <style>{`
        @keyframes flowAlongPath {
          0% { offset-distance: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }
      `}</style>
    </section>
  );
}
